import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useStores } from "stores/setup/use-store";
import EventStep1BasicInfo from "../components/EventStep1BasicInfo";
import { Step2AlertTypeConditions } from "pages/Alert/components/Step2AlertTypeConditions";
import { EventPreferencesStep } from "../components/EventPreferencesStep";
import { CreateEventFormValues, getEventInitialValues } from "../types";
import { useAlertGeospatial } from "pages/Alert/hooks/useAlertGeospatial";
import { useAlertTemplates } from "pages/Alert/hooks/useAlertTemplates";
import { useAlertCreationUsers } from "pages/Alert/hooks/useAlertCreationUsers";
import { DEFAULT_RECEIPT_TYPES } from "pages/Alert/constants";
import {
  DEFAULT_MIN_ADVANCE_TIME,
  DEFAULT_MAX_ADVANCE_TIME,
} from "pages/OceanWeatherIntelligence/InsightAlert/constants/SendConfigConstants";
import { conditionGroupsSchema } from "pages/Alert/util/conditionGroupsSchema";
import { ILocationMenuItem } from "stores/data/operationRuleLocations/OperationRuleLocationModel";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { getMacroRegionOriginFromPolygon } from "util/macroRegion/getMacroRegionOriginFromPolygon";
import { getMacroRegionOriginFromPoint } from "util/macroRegion/getMacroRegionOriginFromPoint";
import { MacroRegion } from "enum/MacroRegion";

interface UseEventCreationDrawerParams {
  isOpen: boolean;
  onEventCreatedSuccess?: () => void;
  showPhoneVerificationModal: boolean;
  prefilledLocation?: ILocationMenuItem | null;
  isLocationLocked?: boolean;
  prefilledDashboardId?: number;
  /** If true, insight block starts active and cannot be toggled off */
  forceInsightEnabled?: boolean;
}

export function useEventCreationDrawer({
  isOpen,
  onEventCreatedSuccess,
  showPhoneVerificationModal,
  prefilledLocation = null,
  isLocationLocked = false,
  prefilledDashboardId,
  forceInsightEnabled = false,
}: UseEventCreationDrawerParams) {
  const {
    dataStores: {
      unitMeasurementStore,
      insightAlertStore,
      operationRuleStore,
      userConfigStore,
      regionStore,
      authStore,
    },
  } = useStores();

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "event",
  });

  // Reuse existing hooks from Alert module
  const templateType = insightAlertStore.createFormValues?.templateType;
  const templates = useAlertTemplates(templateType);
  const geospatial = useAlertGeospatial();
  const users = useAlertCreationUsers();

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userId = authStore.userId;

  // Initial values for the Event form
  const [initialValues, setInitialValues] = useState<CreateEventFormValues>(
    () =>
      getEventInitialValues({
        name: "",
        templateType: "no_category", // Default to "No category"
        customTitle: "",
        customDescription: "",
        channels: ["email", "push_notification", "whatsapp"],
        selectedUsers: [
          {
            id: 0,
            userId,
            userName: userConfigStore.userConfig.name,
            isWhatsappAllowed:
              userConfigStore.userConfig.consentTermWhatsappAlert,
            isPushAllowed: true,
          },
        ],
        minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
        maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
        minimumEventDuration: 60,
        receiptType: DEFAULT_RECEIPT_TYPES,
        locationId: undefined,
        dashboardId: prefilledDashboardId,
        insightId: undefined,
        // Pre-select "Quero ver este evento no gráfico" when dashboard is prefilled or forceInsightEnabled
        block1Active:
          prefilledDashboardId || forceInsightEnabled ? true : undefined,
      }),
  );

  // Validation schema with conditional validation for toggle blocks
  const validationSchema = useMemo(
    () =>
      Yup.object()
        .shape({
          name: Yup.string().required("name is required"),
          channels: Yup.array().when("block2Active", {
            is: true,
            then: (schema) => schema.min(1, "channels is required"),
            otherwise: (schema) => schema,
          }),
          conditionGroups: Yup.mixed().when("restrictionType", {
            is: false, // Permissive — conditions optional
            then: () => Yup.array(),
            otherwise: () => conditionGroupsSchema,
          }),
          sensorAlertConfig: Yup.object().nullable().when({
            is: (val: any) => val && val.sensors && val.sensors.length > 0,
            then: () => Yup.object().shape({
              conditions: conditionGroupsSchema,
            }),
            otherwise: () => Yup.object().nullable(),
          }),
          minimumEventDuration: Yup.number().when("block2Active", {
            is: true,
            then: (schema) =>
              schema.required("minimumEventDuration is required"),
            otherwise: (schema) => schema,
          }),
          block1Active: Yup.boolean(),
          block2Active: Yup.boolean(),
          // Dashboard is always required for Event creation
          dashboardId: Yup.number()
            .required("dashboardRequired")
            .positive("dashboardRequired"),
        })
        .test(
          "at-least-one-block",
          "selectAtLeastOneOption",
          function (values: any) {
            if (!values.block1Active && !values.block2Active) {
              return this.createError({
                path: "block1Active",
                message: "selectAtLeastOneOption",
              });
            }
            return true;
          },
        )
        .test(
          "location-validation",
          "Please provide location information",
          function (values: any) {
            // Check form values first, then fall back to MobX store.
            // The store fallback is necessary because enableReinitialize can reset
            // form values (geofence, locationId) before useFormValuesSync syncs them
            // back, while the store retains the selected location.
            const hasAreaLocation =
              (values.geofence && values.geofence.length > 0) ||
              values.locationId ||
              (insightAlertStore.selectedGeofence &&
                insightAlertStore.selectedGeofence.length > 0) ||
              !!insightAlertStore.selectedInsightLocationId;
            const hasPointLocation =
              (values.macroRegionId && values.stationRegion) ||
              ((insightAlertStore.selectedStationForAlert?.station_id ?? 0) > 0 &&
                !!insightAlertStore.selectedStationForAlert?.region_id);
            const hasCustomPointLocation =
              insightAlertStore.selectedStationForAlert?.station_id === 0;

            if (
              !hasAreaLocation &&
              !hasPointLocation &&
              !hasCustomPointLocation
            ) {
              return this.createError({
                path: "geofence",
                message: "Please provide location information",
              });
            }
            return true;
          },
        )
        .test(
          "advance-time-range-validation",
          "minAdvanceTime must be less than maxAdvanceTime",
          function (values: any) {
            if (!values.block2Active) return true;
            if (values.minAdvanceTime > values.maxAdvanceTime) {
              return this.createError({
                path: "maxAdvanceTime",
                message: "sendConfig.minMaxValidationError",
              });
            }
            return true;
          },
        )
        .test(
          "advance-time-gap-validation",
          "Minimum 1 hour gap required",
          function (values: any) {
            if (!values.block2Active) return true;
            const gap = values.maxAdvanceTime - values.minAdvanceTime;
            if (gap < 60) {
              return this.createError({
                path: "maxAdvanceTime",
                message: "sendConfig.minGapValidationError",
              });
            }
            return true;
          },
        )
        .test(
          "custom-title-required",
          "customTitle is required",
          function (values: any) {
            if (!values.block2Active) return true;
            const hasCustomTitle =
              values.customTitle && values.customTitle.trim().length > 0;
            if (!hasCustomTitle) {
              return this.createError({
                path: "customTitle",
                message: "thisFieldRequired",
              });
            }
            return true;
          },
        )
        .test(
          "sensor-alert-config-validation",
          "Sensor alert conditions required",
          function (values: any) {
            const config = values.sensorAlertConfig;
            if (!config || !config.sensors || config.sensors.length === 0) {
              return true; // No sensor alert configured — valid
            }
            // If sensors are selected, at least one condition group with conditions is required
            const hasConditions =
              config.conditions &&
              config.conditions.length > 0 &&
              config.conditions.some(
                (g: any) => g.conditions && g.conditions.length > 0,
              );
            if (!hasConditions) {
              return this.createError({
                path: "sensorAlertConfig",
                message: "sensorAlertConditionsRequired",
              });
            }
            return true;
          },
        ),
    [
      insightAlertStore.selectedStationForAlert,
      insightAlertStore.selectedGeofence,
      insightAlertStore.selectedInsightLocationId,
    ],
  );

  // Update initial values when prefilledDashboardId changes
  useEffect(() => {
    if (isOpen && prefilledDashboardId) {
      setInitialValues((prev) => ({
        ...prev,
        dashboardId: prefilledDashboardId,
        block1Active: true,
      }));
    }
  }, [isOpen, prefilledDashboardId]);

  // Load data when drawer opens
  useEffect(() => {
    if (isOpen) {
      // Clear potentially stale location data when this is a fresh session.
      // createFormValues being null means the previous session was properly closed,
      // so any remaining store values (selectedStationForAlert, selectedGeofence,
      // selectedInsightLocationId) are leftover from an aborted selection flow and
      // must not be used as fallbacks in location-validation.
      // When returning from selection (createFormValues is non-null), we preserve
      // the store data intentionally.
      if (!insightAlertStore.createFormValues) {
        insightAlertStore.setSelectedStationForAlert(null);
        insightAlertStore.setSelectedGeofence([]);
        insightAlertStore.setSelectedInsightLocationId(null);
      }

      // CRITICAL: Set alertCategory to "insight" IMMEDIATELY when drawer opens
      // This ensures step validation uses the correct mode from the start
      // Without this, the first open would use "standalone" mode (default value)
      // and fail validation even when location is selected
      insightAlertStore.setAlertCategory("insight");

      unitMeasurementStore.getUserUnitMeasurementApi();
      insightAlertStore.setIsGeofenceSavedToLocation(false);
      users.loadAlertUsers(i18n?.language);
      // Load dashboards for Block 1 (only if not already loaded)
      if (operationRuleStore.operationRules.length === 0) {
        operationRuleStore.getOperationRulesApi();
      }
      // Reload alert templates only if not already loaded
      // When returning from sensor selection the templates are still in memory
      if (insightAlertStore.alertTemplates.length === 0) {
        insightAlertStore.getAlertTemplateConfig();
      }

      // Prefill location data if provided
      if (prefilledLocation) {
        // Check if it's an area location (has valid geofence with at least 4 points for a polygon)
        const hasValidGeofence =
          prefilledLocation.geofence && prefilledLocation.geofence.length >= 4;

        // Check if it's a single point stored as geofence (custom point without station)
        const isSinglePointGeofence =
          prefilledLocation.geofence &&
          prefilledLocation.geofence.length > 0 &&
          prefilledLocation.geofence.length < 4 &&
          !prefilledLocation.station;

        if (hasValidGeofence) {
          insightAlertStore.setSelectedGeofence(prefilledLocation.geofence!);
          insightAlertStore.setAlertLocationType("area");
          insightAlertStore.setIsGeofenceSavedToLocation(true);

          // Always set operationRuleLocationId from the location menu item
          // This is needed so the backend creates the event within the correct location,
          // especially for unsaved areas that only have lat/lng (no location.id)
          setInitialValues((prev) => ({
            ...prev,
            operationRuleLocationId: prefilledLocation.id,
          }));

          // Set the locationId from the saved location (location.id) so the event is associated with it
          // Use location.id (the actual saved location ID) not the operationRuleLocation.id
          const savedLocationId = prefilledLocation.location?.id;
          if (savedLocationId) {
            setInitialValues((prev) => ({
              ...prev,
              locationId: savedLocationId,
            }));
            insightAlertStore.setSelectedInsightLocationId(savedLocationId);
            // Show area selector dropdown with the saved area selected
            insightAlertStore.setAreaSelectorVisible(true);
          }

          // Calculate and set macroRegionOrigin from geofence
          const macroRegion = getMacroRegionOriginFromPolygon(
            [prefilledLocation.geofence!],
            regionStore.macroRegions,
            authStore.userMarcroRegions as MacroRegion[],
          );
          if (macroRegion) {
            insightAlertStore.setMacroRegionOrigin(macroRegion);
            // Load environmental variables for conditions step
            insightAlertStore.getEnvironmentalVariables({
              macroRegionOrigin: macroRegion,
            });
          }
        }
        // Handle custom point stored as single geofence coordinate (without station data)
        else if (isSinglePointGeofence) {
          const point = prefilledLocation.geofence![0];

          // Find macroRegion from the point coordinates
          const macroRegion = getMacroRegionOriginFromPoint(
            point.lat,
            point.lng,
            regionStore.macroRegions,
            authStore.userMarcroRegions as MacroRegion[],
          );

          // Create a custom point station from the geofence coordinate
          const customStation: IStation = {
            station_id: 0,
            station_name:
              prefilledLocation.name ||
              `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`,
            region_id: 0,
            station_lat: point.lat,
            station_lon: point.lng,
            station_depth: 0,
            station_depth_unit: "m",
            macro_region_origin: macroRegion || undefined,
          };

          insightAlertStore.setSelectedStationForAlert(customStation);
          insightAlertStore.setAlertLocationType("point");

          // Always set operationRuleLocationId from the location menu item
          setInitialValues((prev) => ({
            ...prev,
            operationRuleLocationId: prefilledLocation.id,
          }));

          // Set the locationId from the saved location (location.id) so the event is associated with it
          // Use location.id (the actual saved location ID) not the operationRuleLocation.id
          const savedLocationId = prefilledLocation.location?.id;
          if (savedLocationId) {
            setInitialValues((prev) => ({
              ...prev,
              locationId: savedLocationId,
            }));
            insightAlertStore.setSelectedInsightLocationId(savedLocationId);
          }

          if (macroRegion) {
            insightAlertStore.setMacroRegionOrigin(macroRegion);
            // Load environmental variables for conditions step
            insightAlertStore.getEnvironmentalVariables({
              macroRegionOrigin: macroRegion,
            });
          }
        }
        // Check if it's a point location (has station)
        else if (prefilledLocation.station) {
          // Check if it's a custom point (station_id = 0 or no regionId)
          const isCustomPoint =
            prefilledLocation.station.id === 0 ||
            !prefilledLocation.station.regionId;

          // Compute macroRegion FIRST so we can pass it to setSelectedStationForAlert
          const macroRegion = getMacroRegionOriginFromPoint(
            prefilledLocation.station.lat,
            prefilledLocation.station.lon,
            regionStore.macroRegions,
            authStore.userMarcroRegions as MacroRegion[],
          );

          const station: IStation = {
            station_id: isCustomPoint ? 0 : prefilledLocation.station.id,
            station_name: prefilledLocation.station.name,
            region_id: prefilledLocation.station.regionId || 0,
            station_lat: prefilledLocation.station.lat,
            station_lon: prefilledLocation.station.lon,
            station_depth: 0,
            station_depth_unit: "m",
          };

          // Pass macroRegionOverride so the store sets macroRegionOrigin BEFORE
          // triggering reloadEnvironmentalVariablesForStation, which will include
          // the stationId for real stations (station_id > 0)
          insightAlertStore.setSelectedStationForAlert(
            station,
            macroRegion || null,
          );
          insightAlertStore.setAlertLocationType("point");

          // Always set operationRuleLocationId from the location menu item
          setInitialValues((prev) => ({
            ...prev,
            operationRuleLocationId: prefilledLocation.id,
          }));

          // For custom points (not real stations), load env vars with only macroRegion
          // since the API doesn't need stationId for custom points
          // For real stations, the reload inside setSelectedStationForAlert already
          // sent the correct call with stationId
          if (macroRegion && isCustomPoint) {
            insightAlertStore.getEnvironmentalVariables({
              macroRegionOrigin: macroRegion,
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unitMeasurementStore, prefilledLocation]);

  // Reactive: load environmental variables whenever macroRegionOrigin changes
  // This covers both prefilled locations AND user-selected locations in Step 1
  // Uses forceReload only when the macroRegion actually changed (different params),
  // NOT when the drawer simply remounts (e.g., returning from sensor selection)
  // Includes station data when available so the API returns station-specific variables
  useEffect(() => {
    const macroRegion = insightAlertStore.macroRegionOrigin;
    if (isOpen && macroRegion) {
      const station = insightAlertStore.selectedStationForAlert;
      const params: {
        macroRegionOrigin: string;
        stationId?: number;
        latitude?: number;
        longitude?: number;
        forceReload?: boolean;
      } = { macroRegionOrigin: macroRegion };

      // Only include stationId for real stations (station_id > 0)
      // Custom points (station_id === 0) and areas only need macroRegion
      if (station && station.station_id > 0) {
        params.stationId = station.station_id;
      }

      // Only force reload when params actually changed (user picked a different location).
      // When the drawer remounts after sensor selection the params are the same,
      // so the cache hit inside getEnvironmentalVariables is sufficient — no need
      // for forceReload which causes a long skeleton display.
      params.forceReload =
        !insightAlertStore.hasMatchingEnvironmentalVariablesParams(params);

      insightAlertStore.getEnvironmentalVariables(params);
    }
  }, [isOpen, insightAlertStore.macroRegionOrigin, insightAlertStore]);

  // Cleanup when drawer closes (if not in selection mode)
  useEffect(() => {
    const isInSelectionMode =
      geospatial.isAreaDrawingMode || geospatial.isPointSelectionMode;
    const isEditOperationInProgress = !!insightAlertStore.operationRuleAlert;

    if (!isOpen && !isInSelectionMode && !isEditOperationInProgress) {
      resetInitialValues();
      templates.resetTemplateSelection();
      geospatial.clearGeofence();
      users.clearUserTableSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset form to initial state
  const resetInitialValues = useCallback(() => {
    insightAlertStore.setOperationRuleAlert(null);
    insightAlertStore.setCreateFormValues(null);
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setMacroRegionOrigin(null);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setAlertTemplates([]);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    insightAlertStore.setAreaSelectorVisible(false);
    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setSelectedStationForAlert(null);
    insightAlertStore.setOriginalStationForAlert(null);
    insightAlertStore.setAlertCategory("insight"); // Events use insight mode for step validation
    insightAlertStore.setSelectedInsight(null);
    insightAlertStore.setSelectedInsightLocationId(null);
    insightAlertStore.setOriginalTemplateConditions(null);
    // NOTE: Do NOT clear locationMenuList here as it's used by EventManagerTab
    // Clear environmental variables so they can be reloaded for the new location
    insightAlertStore.setEnvironmentalVariables([]);
    // Clear sensor selection state
    insightAlertStore.clearSensorSelection();

    setInitialValues(
      getEventInitialValues({
        name: "",
        templateType: undefined, // User must select a risk type
        customTitle: "",
        customDescription: "",
        channels: ["email", "push_notification", "whatsapp"],
        selectedUsers: [
          {
            id: 0,
            userId,
            userName: userConfigStore.userConfig.name,
            isWhatsappAllowed:
              userConfigStore.userConfig.consentTermWhatsappAlert,
            isPushAllowed: true,
          },
        ],
        minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
        maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
        minimumEventDuration: 60,
        receiptType: DEFAULT_RECEIPT_TYPES,
        locationId: undefined,
        dashboardId: prefilledDashboardId,
        insightId: undefined,
        // Preserve block1Active when dashboard is prefilled
        block1Active: prefilledDashboardId ? true : undefined,
      }),
    );
  }, [
    insightAlertStore,
    userId,
    userConfigStore.userConfig.name,
    userConfigStore.userConfig.consentTermWhatsappAlert,
    prefilledDashboardId,
  ]);

  // Define steps for the Event creation drawer
  // Step 1: Basic Info (reuse from Alert)
  // Step 2: Conditions (reuse from Alert)
  // Step 3: Preferences (new EventPreferencesStep with toggle blocks)
  const STEPS = useMemo(
    () => [
      {
        title: t("step1Location"),
        component: EventStep1BasicInfo,
      },
      {
        title: t("step2Conditions"),
        component: Step2AlertTypeConditions,
      },
      {
        title: t("step3Preferences"),
        component: EventPreferencesStep,
      },
    ],
    [t],
  );

  // Memoize the derived initialValues to prevent creating a new object on every render.
  // Without memoization, getEventInitialValues() creates a new object reference each render,
  // which causes Formik's enableReinitialize to run its effect on every render (since the
  // dependency changes). Combined with the useFormValuesSync 300ms debounce cycle, this can
  // cause enableReinitialize to reset form values to a stale createFormValues snapshot
  // (e.g., with only the default user) before the latest selections are synced.
  // This matches the stable-reference pattern used by useAlertFormData in the Alert flow.
  const derivedInitialValues = useMemo(
    () =>
      insightAlertStore.createFormValues
        ? getEventInitialValues(insightAlertStore.createFormValues)
        : initialValues,
    [insightAlertStore.createFormValues, initialValues],
  );

  return {
    t,
    initialValues: derivedInitialValues,
    validationSchema,
    resetInitialValues,
    STEPS,
    isSubmitting,
    setIsSubmitting,
    // Re-export from other hooks
    ...templates,
    ...geospatial,
    ...users,
  };
}
